import React, { useEffect, useRef, useCallback } from 'react';
import { features } from '../../data/features';
import FeatureItem from './FeatureItem';
import RiveAnimationStatic from '../../components/WebsiteUI/RiveAnimationStatic';
import styles from './FeaturesSection.module.css';

const RiveAnimation = React.lazy(() => import('../../components/WebsiteUI/RiveAnimation'));

const FeaturesSection = () => {
  const featureItemsRef = useRef([]);
  const animationWrapperRef = useRef(null);
  const activeElementRef = useRef(null);
  const currentStateRef = useRef('HERO');
  const tickingRef = useRef(false);

  const updateActiveFeature = useCallback(() => {
    const featureItems = featureItemsRef.current.filter(Boolean);
    const animationWrapper = animationWrapperRef.current;
    const viewportCenter = window.innerHeight / 2;
    let closestElement = null;
    let minDistance = Infinity;
    let anyFeatureVisible = false;

    featureItems.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2;
      const distance = Math.abs(elementCenter - viewportCenter);

      if (rect.bottom > 0 && rect.top < window.innerHeight && distance < minDistance) {
        minDistance = distance;
        closestElement = el;
      }

      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        if (el === closestElement || distance < window.innerHeight * 0.4) {
          el.classList.add(styles['features-section__item--visible']);
          el.classList.remove(styles['features-section__item--exiting']);
          anyFeatureVisible = true;
        }
      } else if (rect.bottom <= 0) {
        el.classList.remove(styles['features-section__item--visible']);
        el.classList.add(styles['features-section__item--exiting']);
      } else {
        el.classList.remove(styles['features-section__item--visible']);
        el.classList.remove(styles['features-section__item--exiting']);
      }
    });

    if (animationWrapper && featureItems.length > 0) {
      const firstFeature = featureItems[0];
      const firstFeatureRect = firstFeature.getBoundingClientRect();
      const firstFeatureInViewport = firstFeatureRect.bottom > 0 && firstFeatureRect.top < window.innerHeight;
      const firstFeaturePassed = firstFeatureRect.bottom <= 0;
      const shouldShow = firstFeatureInViewport || (firstFeaturePassed && anyFeatureVisible);

      if (shouldShow) {
        animationWrapper.classList.add(styles['features-section__animation--visible']);
      } else {
        animationWrapper.classList.remove(styles['features-section__animation--visible']);
      }
    }

    if (closestElement && closestElement !== activeElementRef.current) {
      activeElementRef.current = closestElement;
      const newState = closestElement.dataset.riveState || 'HERO';
      if (newState !== currentStateRef.current) {
        currentStateRef.current = newState;
        window.dispatchEvent(
          new CustomEvent('rive-state-change', { detail: newState })
        );
      }
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!tickingRef.current) {
        requestAnimationFrame(() => {
          updateActiveFeature();
          tickingRef.current = false;
        });
        tickingRef.current = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    updateActiveFeature();

    return () => window.removeEventListener('scroll', onScroll);
  }, [updateActiveFeature]);

  return (
    <section className={styles['features-section']} id="features">
      <div className={styles['features-section__bg']} aria-hidden="true">
        <div className={styles['features-section__bg-color']}></div>
      </div>

      {/* Desktop */}
      <div className={`${styles['features-section__container']} ${styles['features-section__container--desktop']}`}>
        <div className={styles['features-section__list']}>
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={styles['features-section__item']}
              id={feature.id}
              data-rive-state={feature.riveState}
              ref={(el) => (featureItemsRef.current[index] = el)}
            >
              <FeatureItem
                title={feature.title}
                subtitle={feature.subtitle}
                description={feature.description}
              />
            </div>
          ))}
        </div>
        <div className={styles['features-section__animation']} ref={animationWrapperRef}>
          <div className={styles['features-section__animation-wrapper']}>
            <React.Suspense fallback={null}>
              <RiveAnimation className={styles['features-section__rive']} />
            </React.Suspense>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className={`${styles['features-section__container']} ${styles['features-section__container--mobile']}`}>
        {features.map((feature) => (
          <div
            key={`${feature.id}-mobile`}
            className={styles['features-section__mobile-item']}
            id={`${feature.id}-mobile`}
            data-rive-state={feature.riveState}
          >
            <div className={styles['features-section__mobile-animation']}>
              <RiveAnimationStatic state={feature.riveState} className={styles['features-section__rive']} />
            </div>
            <div className={styles['features-section__mobile-content']}>
              <FeatureItem
                title={feature.title}
                subtitle={feature.subtitle}
                description={feature.description}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeaturesSection;
